import { HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import { Observable, map } from 'rxjs';

import { mapCheckoutSessionResponse, mapOrderResponse, mapShippingRateResponse } from '@core/models/order.mapper';
import { CheckoutSessionResponse, OrderResponse, ShippingRateResponse } from '@core/models/order.response';
import { CheckoutSessionState, OrderState, ShippingRateState } from '@core/models/order.state';
import { PaginatedResult } from '@core/models/paginated.response';
import { ApiService } from '@core/services/api.service';

import { OrderRepository, PlaceOrderRequest } from './order.repository';

@Injectable()
export class OrderHttpRepository implements OrderRepository {
  private readonly api = inject(ApiService);

  startCheckout(ownerKey: string): Observable<CheckoutSessionState> {
    return this.api.post<CheckoutSessionResponse>('checkout/start', { ownerKey }).pipe(map(mapCheckoutSessionResponse));
  }

  getShippingRates(storeId: number, itemsSubtotal: number): Observable<ShippingRateState[]> {
    return this.api
      .get<ShippingRateResponse[]>(`checkout/shipping-rates?storeId=${storeId}&itemsSubtotal=${itemsSubtotal}`)
      .pipe(map(rates => rates.map(mapShippingRateResponse)));
  }

  getCheckoutSession(sessionId: number): Observable<CheckoutSessionState | null> {
    return this.api
      .get<CheckoutSessionResponse | null>(`checkout/sessions/${sessionId}`)
      .pipe(map(response => (response ? mapCheckoutSessionResponse(response) : null)));
  }

  releaseCheckout(sessionId: number): Observable<void> {
    return this.api.delete<void>(`checkout/sessions/${sessionId}`);
  }

  placeOrder(request: PlaceOrderRequest): Observable<OrderState> {
    return this.api.post<OrderResponse>('orders', request).pipe(map(mapOrderResponse));
  }

  listOrders(shopperId: number, pageNumber: number, pageSize: number): Observable<PaginatedResult<OrderState>> {
    const params = new HttpParams().set('ShopperId', shopperId).set('PageNumber', pageNumber).set('PageSize', pageSize);
    return this.api
      .get<PaginatedResult<OrderResponse>>('orders', params)
      .pipe(map(page => ({ ...page, items: page.items.map(mapOrderResponse) })));
  }

  getOrder(orderRef: string): Observable<OrderState | null> {
    return this.api
      .get<OrderResponse | null>(`orders/${orderRef}`)
      .pipe(map(response => (response ? mapOrderResponse(response) : null)));
  }

  findByRefAndEmail(orderRef: string, email: string): Observable<OrderState | null> {
    const params = new HttpParams().set('OrderRef', orderRef).set('Email', email);
    return this.api
      .get<OrderResponse | null>('orders/lookup', params)
      .pipe(map(response => (response ? mapOrderResponse(response) : null)));
  }
}
